import type { DisciplineSimple } from '../model/interfaces'
import type { ApiResponse } from '@shared/api'

import { useCommonStore } from '@shared/model'
import { getAxiosErrorMessage } from '@shared/api'
import { useAdminRoutes } from '@shared/lib/routes'
import { useToast } from '@shared/ui/toast'

interface DisciplineMergeProfilesApiOptions {
  disciplineId: string | number
  disciplines: number[]
  onStart?: () => void
  onSuccess?: (discipline: DisciplineSimple) => void
  onError?: (message: string) => void
  onFinish?: () => void
}

/** `POST /admin-api/disciplines/{discipline}/merge-profiles` — the URL discipline survives, the listed
 *  ones are merged into it and deleted. */
export const useDisciplineMergeProfilesApi = async (
  options: DisciplineMergeProfilesApiOptions,
): Promise<DisciplineSimple | null> => {
  const { $axios } = useNuxtApp()
  const toast = useToast()
  const commonStore = useCommonStore()

  const url = useAdminRoutes('admin.disciplines.api.merge-profiles', { discipline_id: options.disciplineId })

  options.onStart?.()
  commonStore.setLoading(true)

  try {
    if (!url) {
      const msg = 'Unexpected error. Invalid request URL.'
      toast.error(msg)
      options.onError?.(msg)
      return null
    }

    const { data } = await $axios.post<ApiResponse>(url, { disciplines: options.disciplines })
    const discipline: DisciplineSimple = data.data

    toast.success({ title: 'Success!', description: 'Selected disciplines were successfully merged' })
    options.onSuccess?.(discipline)

    return discipline
  } catch (err: unknown) {
    const msg = getAxiosErrorMessage(err)
    toast.error({ title: 'Error!', description: msg })
    options.onError?.(msg)

    return null
  } finally {
    commonStore.setLoading(false)
    options.onFinish?.()
  }
}
